/**
 * @fileoverview HypaySuccessPage - עמוד הצלחה לתשלומי כספית
 * @description עמוד שמוצג לאחר השלמת תשלום מוצלח דרך כספית
 */
import { useState, useEffect } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { 
  CheckCircle, 
  Receipt, 
  Home, 
  ShoppingBag,
  Download,
  Mail,
  Loader2,
  AlertCircle
} from "lucide-react";

const HypaySuccessPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  
  const [paymentData, setPaymentData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  // קבלת פרמטרים מכספית
  const transactionId = searchParams.get('Id');
  const ccode = searchParams.get('CCode');
  const amount = searchParams.get('Amount');
  const approvalCode = searchParams.get('ACode');
  const orderId = searchParams.get('Order');
  const invoiceNumber = searchParams.get('Hesh');

  /**
   * 🔍 אימות והצגת פרטי התשלום
   */
  useEffect(() => {
    const verifyPayment = async () => {
      if (!transactionId || ccode !== '0') {
        setError('פרמטרי תשלום לא תקינים');
        setIsLoading(false);
        return;
      }

      try {
        // אימות נוסף עם השרת
        const response = await fetch(`/hypay/status/${transactionId}`, {
          credentials: 'include'
        });

        if (response.ok) {
          const data = await response.json();
          setPaymentData(data);
        } else {
          throw new Error('שגיאה באימות התשלום');
        }
      } catch (error) {
        console.error('Error verifying payment:', error);
        setError(error.message);
      } finally {
        setIsLoading(false);
      }
    };

    verifyPayment();
  }, [transactionId, ccode]);

  /**
   * 📧 שליחת חשבונית מחדש
   */
  const resendInvoice = async () => {
    try {
      const response = await fetch(`/hypay/generate-invoice/${transactionId}`, {
        credentials: 'include'
      });

      if (response.ok) {
        const data = await response.json();
        if (data.invoiceUrl) {
          window.open(data.invoiceUrl, '_blank');
        }
      }
    } catch (error) {
      console.error('Error generating invoice:', error);
    }
  };

  /**
   * 🎨 מסך טעינה
   */
  if (isLoading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-16 h-16 text-blue-600 animate-spin mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-gray-900 mb-2">
            מאמת פרטי תשלום...
          </h2>
          <p className="text-gray-600">
            אנא המתן רגע
          </p>
        </div>
      </div>
    );
  }

  /**
   * 🚨 מסך שגיאה
   */
  if (error) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="max-w-md mx-auto text-center">
          <AlertCircle className="w-16 h-16 text-red-600 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-red-900 mb-2">
            שגיאה באימות התשלום
          </h2>
          <p className="text-red-700 mb-6">
            {error}
          </p>
          <button
            onClick={() => navigate('/')}
            className="bg-red-600 text-white px-6 py-3 rounded-lg hover:bg-red-700 transition-colors"
          >
            חזור לעמוד הבית
          </button>
        </div>
      </div>
    );
  }

  /**
   * 🎉 מסך הצלחה ראשי
   */
  return (
    <div className="min-h-screen bg-white">
      <div className="container mx-auto px-4 py-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="max-w-2xl mx-auto"
        >
          {/* הודעת הצלחה ראשית */}
          <div className="bg-white rounded-lg shadow-lg p-8 mb-6">
            <div className="text-center">
              <motion.div
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ delay: 0.2, type: "spring", stiffness: 200 }}
              >
                <CheckCircle className="w-20 h-20 text-green-600 mx-auto mb-4" />
              </motion.div>
              
              <h1 className="text-3xl font-bold text-green-900 mb-2">
                התשלום בוצע בהצלחה! 🎉
              </h1>
              
              <p className="text-green-700 text-lg mb-6">
                תודה על הרכישה. ההזמנה שלך אושרה ותעובד בקרוב.
              </p>
            </div>

            {/* פרטי העסקה */}
            <div className="bg-gray-50 rounded-lg p-6 mb-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">
                פרטי העסקה
              </h3>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <span className="text-sm text-gray-600">מספר עסקה</span>
                  <p className="font-semibold text-gray-900">{transactionId}</p>
                </div>
                
                <div>
                  <span className="text-sm text-gray-600">מספר אישור</span>
                  <p className="font-semibold text-gray-900">{approvalCode}</p>
                </div>
                
                <div>
                  <span className="text-sm text-gray-600">מספר הזמנה</span>
                  <p className="font-semibold text-gray-900">{orderId}</p>
                </div>
                
                <div>
                  <span className="text-sm text-gray-600">סכום ששולם</span>
                  <p className="font-semibold text-green-600 text-lg">
                    ₪{parseFloat(amount).toFixed(2)}
                  </p>
                </div>
                
                {invoiceNumber && invoiceNumber !== '0' && (
                  <div>
                    <span className="text-sm text-gray-600">מספר חשבונית</span>
                    <p className="font-semibold text-gray-900">{invoiceNumber}</p>
                  </div>
                )}
                
                <div>
                  <span className="text-sm text-gray-600">תאריך ושעה</span>
                  <p className="font-semibold text-gray-900">
                    {new Date().toLocaleDateString('he-IL')} {new Date().toLocaleTimeString('he-IL')}
                  </p>
                </div>
              </div>
            </div>

            {/* מידע נוסף */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
              <div className="flex items-start space-x-3 rtl:space-x-reverse">
                <Mail className="w-5 h-5 text-blue-600 mt-0.5" />
                <div>
                  <h4 className="font-medium text-blue-900">אישור נשלח באימייל</h4>
                  <p className="text-blue-700 text-sm">
                    אישור התשלום וחשבונית נשלחו לכתובת האימייל שלך
                  </p>
                </div>
              </div>
            </div>

            {/* פעולות */}
            <div className="flex flex-col sm:flex-row gap-4">
              <motion.button
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                onClick={() => navigate('/')}
                className="flex-1 bg-blue-600 text-white py-3 px-6 rounded-lg font-semibold
                         hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2
                         flex items-center justify-center space-x-2 rtl:space-x-reverse"
              >
                <Home className="w-5 h-5" />
                <span>חזור לעמוד הבית</span>
              </motion.button>

              <motion.button
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                onClick={() => navigate('/products')}
                className="flex-1 bg-gray-600 text-white py-3 px-6 rounded-lg font-semibold
                         hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2
                         flex items-center justify-center space-x-2 rtl:space-x-reverse"
              >
                <ShoppingBag className="w-5 h-5" />
                <span>המשך קנייה</span>
              </motion.button>

              {invoiceNumber && invoiceNumber !== '0' && (
                <motion.button
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={resendInvoice}
                  className="flex-1 bg-green-600 text-white py-3 px-6 rounded-lg font-semibold
                           hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2
                           flex items-center justify-center space-x-2 rtl:space-x-reverse"
                >
                  <Download className="w-5 h-5" />
                  <span>הורד חשבונית</span>
                </motion.button>
              )}
            </div>
          </div>

          {/* מידע נוסף על התהליך */}
          <div className="bg-white rounded-lg shadow-lg p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">
              מה קורה עכשיו?
            </h3>
            
            <div className="space-y-4">
              <div className="flex items-center space-x-3 rtl:space-x-reverse">
                <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center">
                  <CheckCircle className="w-5 h-5 text-green-600" />
                </div>
                <div>
                  <h4 className="font-medium text-gray-900">התשלום אושר</h4>
                  <p className="text-gray-600 text-sm">
                    התשלום עובד בהצלחה דרך כספית
                  </p>
                </div>
              </div>

              <div className="flex items-center space-x-3 rtl:space-x-reverse">
                <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                  <Receipt className="w-5 h-5 text-blue-600" />
                </div>
                <div>
                  <h4 className="font-medium text-gray-900">יצירת חשבונית</h4>
                  <p className="text-gray-600 text-sm">
                    חשבונית מס אוטומטית נוצרה ונשלחה אליך
                  </p>
                </div>
              </div>

              <div className="flex items-center space-x-3 rtl:space-x-reverse">
                <div className="w-8 h-8 bg-yellow-100 rounded-full flex items-center justify-center">
                  <ShoppingBag className="w-5 h-5 text-yellow-600" />
                </div>
                <div>
                  <h4 className="font-medium text-gray-900">הכנת ההזמנה</h4>
                  <p className="text-gray-600 text-sm">
                    ההזמנה שלך תעובד ותכן לשליחה בקרוב
                  </p>
                </div>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default HypaySuccessPage; 